import { Link, useNavigate } from "react-router-dom";
import { LogOut, Settings } from "lucide-react";
import { useAuth } from "@workos-inc/authkit-react";
import { getTenantIdentity } from "@/lib/tenant";
import { useUserSettings } from "@/hooks/useUserSettings";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { myjarvisLogoUrl } from "./myjarvis-logo";
import { navItems, navSections, NavLink } from "./nav-items";

// Preview mode (set in /settings) collapses the sidebar to the Knowledge
// Base item only. Filtering by the route slug keeps the gate decoupled
// from item ordering or label changes in nav-items.tsx.
const PREVIEW_ALLOWED_ROUTES = new Set(["/knowledge-base"]);

export function CrmSidebar({ onCollapse }: { onCollapse?: () => void }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { displayName: tenantDisplayName } = getTenantIdentity();
  const { settings } = useUserSettings();

  const fullName = [user?.firstName, user?.lastName]
    .filter((v): v is string => Boolean(v))
    .join(" ");
  const displayName = fullName || user?.email || "User";
  const avatarUrl = user?.profilePictureUrl ?? undefined;
  const initial = displayName.charAt(0).toUpperCase();

  const previewNavItems = navItems.filter((item) =>
    PREVIEW_ALLOWED_ROUTES.has(item.to),
  );

  return (
    <aside className="hidden h-svh w-56 shrink-0 flex-col border-r bg-sidebar md:flex">
      <div className="flex items-center gap-2 px-4 py-4">
        <button
          type="button"
          onClick={onCollapse}
          aria-label="Collapse sidebar"
          title="Collapse sidebar"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-opacity hover:opacity-80"
        >
          <img
            src={myjarvisLogoUrl}
            alt={`${tenantDisplayName} logo`}
            className="h-8 w-8 rounded-lg object-cover"
          />
        </button>
        {/* The product name, not the tenant's. Tailormind (Erez, 2026-07-28). */}
        <Link to="/" className="text-sm font-semibold truncate">
          Tailormind
        </Link>
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 px-2 overflow-y-auto pt-2 pb-4">
        {settings.preview_mode
          ? previewNavItems.map((item) => (
              <NavLink key={item.to} item={item} />
            ))
          : navSections.map((section) => (
              <div key={section.title} className="mt-5 flex flex-col gap-0.5 first:mt-1">
                <div className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/50">
                  {section.title}
                </div>
                {section.items.map((item) => (
                  <NavLink key={item.to} item={item} />
                ))}
              </div>
            ))}
      </nav>

      <div className="px-2 pb-3 pt-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
              <Avatar className="h-7 w-7">
                {avatarUrl && <AvatarImage src={avatarUrl} />}
                <AvatarFallback className="text-xs">{initial}</AvatarFallback>
              </Avatar>
              <span className="truncate">{displayName}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-48">
            <DropdownMenuItem
              onClick={() => navigate("/settings")}
              className="cursor-pointer"
            >
              <Settings className="h-4 w-4 me-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => signOut()}
              className="cursor-pointer"
            >
              <LogOut className="h-4 w-4 me-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
